/**
 * The date a document actually happened, as opposed to the day somebody found
 * time to type it in.
 *
 * With 31 live sites and paperwork arriving in bundles, most entries are made
 * late. The system has to record when the goods arrived, not when the clerk
 * caught up — otherwise the weekly consumption figures describe the office's
 * typing schedule rather than the sites' material usage.
 *
 * The browser stops the obvious slips with `min` and `max`, and the database
 * refuses the same two things independently, because a date typed on a phone
 * keyboard or arriving through the offline queue never touched this component.
 */
import { useEffect, useState } from 'react'
import { CalendarClock } from 'lucide-react'
import { Field, Input } from '@/components/ui'
import { supabase } from '@/lib/supabase'

/** Cached for the session — it is a company-wide setting that never moves. */
let cachedLimit: number | null = null

export function useBackdateLimit(): number {
  const [days, setDays] = useState(cachedLimit ?? 1095)
  useEffect(() => {
    if (cachedLimit !== null) return
    let live = true
    supabase.from('company_profile').select('backdate_limit_days').eq('id', 1).maybeSingle()
      .then(({ data }) => {
        const v: number = data?.backdate_limit_days ?? 1095
        cachedLimit = v
        if (live) setDays(v)
      })
    return () => { live = false }
  }, [])
  return days
}

export const todayIso = () => new Date().toLocaleDateString('en-CA')   // YYYY-MM-DD, local

function daysBetween(iso: string) {
  const d = new Date(iso + 'T00:00:00')
  const t = new Date(todayIso() + 'T00:00:00')
  return Math.round((t.getTime() - d.getTime()) / 864e5)
}

export function DocumentDate({ label, value, onChange, hint, required = true, className }: {
  label: string
  value: string
  onChange: (v: string) => void
  hint?: string
  required?: boolean
  className?: string
}) {
  const limit = useBackdateLimit()
  const min = new Date(Date.now() - limit * 864e5).toLocaleDateString('en-CA')
  const late = value ? daysBetween(value) : 0

  // WHERE WILL THE FIGURES ACTUALLY LAND?
  //
  // This component cannot work that out. Whether a month is still open, and
  // whether this particular user is inside the Accounts grace window, are facts
  // only the database holds. The old wording here asserted "stock will move on
  // this date, not today" — which became the exact OPPOSITE of the truth the
  // day the period lock shipped. So now it asks.
  const [posting, setPosting] = useState<{ posting_date: string; deferred: boolean; reason: string | null } | null>(null)

  useEffect(() => {
    if (!value || late < 1) { setPosting(null); return }
    let live = true
    void supabase.rpc('rpc_posting_date_for', { p_document_date: value })
      .then(({ data }) => { if (live && data) setPosting(data as typeof posting) })
    return () => { live = false }
  }, [value, late])

  return (
    <Field
      label={label}
      required={required}
      className={className}
      hint={hint ?? `Defaults to today. Anything back to ${ddmy(min)} is accepted.`}
    >
      <Input type="date" value={value} min={min} max={todayIso()}
             onChange={(e) => onChange(e.target.value)} />
      {late >= 1 && (
        <p className={`mt-1 flex items-start gap-1 text-xs font-medium ${
          posting?.deferred ? 'text-orange-700' : 'text-amber-700'}`}>
          <CalendarClock className="mt-0.5 h-3 w-3 shrink-0" />
          <span>
            Back-dated by {late} day{late === 1 ? '' : 's'}.{' '}
            {posting?.deferred ? (
              <>
                That month is closed, so the figures — <strong>including stock</strong> —
                will post on <strong>{ddmy(posting.posting_date)}</strong>, not on the date
                above. The document keeps its true date.
              </>
            ) : (
              <>
                That month is still open, so the figures post on this date and the affected
                weekly totals are recalculated automatically.
              </>
            )}
          </span>
        </p>
      )}
    </Field>
  )
}

const ddmy = (iso: string) => {
  const [y, m, d] = iso.split('-')
  return `${d}-${m}-${y}`
}
